// src/components/organisms/VisualFilterExplorer/VisualFilterExplorer.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import {
  CollectionsBookmark as CollectionIcon,
  History as HistoryIcon,
  Label as TagIcon,
  Search as SearchIcon,
  Source as SourceIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalElevation,
  operationalFontSizes,
  operationalRadii,
  operationalText
} from '../../../design-system/tokens';

const fallbackImage = 'https://media.mensuremidia.com.br/system/fallbacks/mensure-thumbnail.png';

const normalizeLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return (value.name || value.title || value.label || value.slug || '').toString().trim();
};

const getArticleImage = (article) => (
  article?.imageUrl || article?.image || article?.thumbnail || fallbackImage
);

const getArticleTags = (article) => (
  Array.isArray(article?.tags)
    ? article.tags.map(normalizeLabel).filter(Boolean)
    : []
);

const getArticleCollections = (article) => {
  const collections = article?.collections || article?.collectionIds || article?.collection || [];
  return (Array.isArray(collections) ? collections : [collections])
    .map(normalizeLabel)
    .filter(Boolean);
};

const buildFacetItems = (articles, getValues, type, fallbackIcon) => {
  const map = new Map();

  articles.forEach((article) => {
    getValues(article).forEach((label) => {
      const current = map.get(label) || {
        id: `${type}-${label}`,
        label,
        type,
        count: 0,
        images: [],
        icon: fallbackIcon
      };

      current.count += 1;
      if (current.images.length < 4) {
        current.images.push(getArticleImage(article));
      }
      map.set(label, current);
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.count - a.count);
};

const VisualFilterCard = ({ item, selected, onToggle }) => {
  const theme = useTheme();
  const Icon = item.icon;
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onToggle(item)}
      sx={{
        width: '100%',
        minHeight: 92,
        border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: `${operationalRadii.surface}px`,
        backgroundColor: selected
          ? chipTokens.backgroundHover
          : (theme.palette.mode === 'dark' ? operationalComponentSurfaces.filterPanel.dark : operationalComponentSurfaces.filterPanel.light),
        color: theme.palette.text.primary,
        display: 'grid',
        gridTemplateColumns: '92px minmax(0, 1fr)',
        gap: 1.5,
        alignItems: 'center',
        p: 1,
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: selected
          ? operationalElevation.surface
          : 'none',
        transition: 'border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.mode === 'dark'
            ? chipTokens.background
            : chipTokens.background
        }
      }}
    >
      <Box
        sx={{
          width: 92,
          height: 72,
          borderRadius: `${operationalRadii.control}px`,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: item.images.length > 1 ? '1fr 1fr' : '1fr',
          gridTemplateRows: item.images.length > 2 ? '1fr 1fr' : '1fr',
          bgcolor: theme.palette.action.hover
        }}
      >
        {item.images.length ? item.images.map((src, index) => (
          <Box
            component="img"
            src={src}
            alt=""
            key={`${item.id}-image-${index}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        )) : (
          <Box sx={{ display: 'grid', placeItems: 'center', color: operationalText.light.muted }}>
            <Icon />
          </Box>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <Icon sx={{ fontSize: 17, color: selected ? theme.palette.primary.main : theme.palette.text.secondary }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 850,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {item.label}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: operationalFontSizes.xs }}>
          {item.count} artigo{item.count !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

VisualFilterCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    images: PropTypes.arrayOf(PropTypes.string),
    icon: PropTypes.elementType.isRequired
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};

const VisualFilterSection = ({
  title,
  icon,
  items,
  selectedIds,
  expanded,
  onToggle,
  onToggleExpanded,
  previewLimit = 8
}) => {
  if (!items.length) return null;

  const visibleItems = expanded ? items : items.slice(0, previewLimit);
  const hasMore = items.length > previewLimit;

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Chip
            label={items.length}
            size="small"
            variant="outlined"
            sx={{ height: 22, borderRadius: `${operationalRadii.control}px`, fontWeight: 800 }}
          />
        </Box>
        {hasMore && (
          <Box
            component="button"
            type="button"
            onClick={onToggleExpanded}
            sx={{
              border: 0,
              background: 'transparent',
              color: 'primary.main',
              fontSize: operationalFontSizes.sm,
              fontWeight: 900,
              cursor: 'pointer',
              px: 0.5,
              py: 0.25,
              borderRadius: `${operationalRadii.control}px`,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {expanded ? 'Ver menos' : `Ver todas (${items.length})`}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))'
          },
          gap: 1.25
        }}
      >
        {visibleItems.map(item => (
          <VisualFilterCard
            item={item}
            selected={selectedIds.includes(item.id)}
            onToggle={onToggle}
            key={item.id}
          />
        ))}
      </Box>
    </Box>
  );
};

VisualFilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  previewLimit: PropTypes.number
};

const VisualFilterExplorer = ({ articles = [], brandName }) => {
  const theme = useTheme();
  const [query, setQuery] = React.useState('');
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [expandedSections, setExpandedSections] = React.useState({});
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];

  const sections = React.useMemo(() => {
    const recentItems = articles.slice(0, 12).map((article) => ({
      id: `recent-${article._id || article.title}`,
      label: article.title || 'Artigo recente',
      type: 'recent',
      count: 1,
      images: [getArticleImage(article)],
      icon: HistoryIcon
    }));

    const tagItems = buildFacetItems(articles, getArticleTags, 'tag', TagIcon);
    const collectionItems = buildFacetItems(articles, getArticleCollections, 'collection', CollectionIcon);
    const sourceItems = buildFacetItems(
      articles,
      article => [article.siteName || article.source || article.vehicle || article.mediaType].filter(Boolean),
      'source',
      SourceIcon
    );

    return [
      { title: 'Pesquisas recentes', icon: <HistoryIcon fontSize="small" color="primary" />, items: recentItems },
      { title: 'Coleções', icon: <CollectionIcon fontSize="small" color="primary" />, items: collectionItems },
      { title: 'Tags em destaque', icon: <TagIcon fontSize="small" color="primary" />, items: tagItems },
      { title: 'Fontes', icon: <SourceIcon fontSize="small" color="primary" />, items: sourceItems }
    ];
  }, [articles]);

  const filteredSections = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return sections;

    return sections.map(section => ({
      ...section,
      items: section.items.filter(item => item.label.toLowerCase().includes(normalizedQuery))
    }));
  }, [query, sections]);

  const selectedIds = selectedItems.map(item => item.id);
  const selectedCount = selectedItems.reduce((sum, item) => sum + item.count, 0);

  const handleToggle = (item) => {
    setSelectedItems((current) => (
      current.some(selected => selected.id === item.id)
        ? current.filter(selected => selected.id !== item.id)
        : [...current, item]
    ));
  };

  const handleToggleSection = (title) => {
    setExpandedSections((current) => ({
      ...current,
      [title]: !current[title]
    }));
  };

  return (
    <Box
      sx={{
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark'
          ? operationalComponentSurfaces.filterPanel.dark
          : operationalComponentSurfaces.filterPanel.light,
        p: { xs: 2, md: 2.5 },
        display: 'grid',
        gap: 2,
        boxShadow: theme.palette.mode === 'dark'
          ? operationalElevation.raised
          : operationalElevation.surface
      }}
    >
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 0.5 }}>
            Filtros visuais
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Protótipo para explorar tags, coleções e fontes sem sair dos cards{brandName ? ` de ${brandName}` : ''}.
          </Typography>
        </Box>
        <Chip
          icon={<TrendingIcon />}
          label={selectedItems.length ? `${selectedItems.length} filtros · ${selectedCount} relações` : 'Exploração visual'}
          color={selectedItems.length ? 'primary' : 'default'}
          variant={selectedItems.length ? 'filled' : 'outlined'}
          sx={{ fontWeight: 800, borderRadius: `${operationalRadii.control}px` }}
        />
      </Box>

      <TextField
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Buscar tags, coleções, fontes ou pesquisas recentes..."
        fullWidth
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: `${operationalRadii.surface}px`,
            bgcolor: theme.palette.mode === 'dark'
              ? chipTokens.background
              : chipTokens.background
          }
        }}
      />

      {selectedItems.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
            Selecionados
          </Typography>
          {selectedItems.map(item => (
            <Chip
              label={item.label}
              onDelete={() => handleToggle(item)}
              size="small"
              color="primary"
              key={`selected-${item.id}`}
              sx={{ borderRadius: `${operationalRadii.control}px`, fontWeight: 800 }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'grid', gap: 2.25 }}>
        {filteredSections.map(section => (
          <VisualFilterSection
            title={section.title}
            icon={section.icon}
            items={section.items}
            selectedIds={selectedIds}
            expanded={Boolean(expandedSections[section.title]) || Boolean(query.trim())}
            onToggle={handleToggle}
            onToggleExpanded={() => handleToggleSection(section.title)}
            key={section.title}
          />
        ))}
      </Box>
    </Box>
  );
};

VisualFilterExplorer.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.object),
  brandName: PropTypes.string
};

export default VisualFilterExplorer;
